import type { TowerDesign } from "@/game/towerSpec";
import type { Section } from "@/physics/model";

/**
 * Montville 370 ft guyed lattice tower (Old Colchester Rd, Montville CT / AT&T
 * CT2049), from the stamped tnxTower structural analysis in reference_towers/
 * 02_engineering_drawings/CT_Montville_OldColchester_guyed_lattice_370ft_structural.pdf.
 *
 * EXACT data (report text):
 *   - 370 ft (112.78 m), THREE-FACE (triangular) guyed steel lattice, 15 vertical
 *     sections. Face width 5.00 ft at base AND top (constant) → circumradius
 *     5·0.3048/√3 = 0.880 m.
 *   - Legs: SOLID ROUND pipe, ASTM A36. Diagonal/horizontal bracing: steel angle
 *     + solid round pipe, ASTM A36. Bolted flange-plate section joints.
 *   - Guys transfer to TWELVE concrete anchor blocks = 4 guy levels × 3 azimuths.
 *     EXACT anchor radii (report uplift checks): 114.41, 193.65, 224.79, 247.15 ft
 *     (34.87, 59.02, 68.52, 75.33 m). Guy attach heights on the mast estimated
 *     (not cleanly tabulated in the extract) — flagged below.
 *   - Design: TIA-222-G-2005, Exposure C, 105 mph basic 3-sec gust, 0.75" radial
 *     ice (increasing with height). Appurtenances: search antenna @370 ft, 8-ft
 *     microwave dish @355 ft, 20' whip, 7/8" coax on a leg face.
 *   - Guy strand size not stated in the extract → 1/2" EHS assumed (flagged).
 */

// ~2" solid round leg (A36) — solid round pipe legs of a 5-ft-face guyed lattice.
const LEG: Section = { name: "Montville solid-round leg (~2in)", A: 2.03e-3, I: 5e-6, dragWidth: 0.051 };

const H = 112.78; // 370 ft

export const design: TowerDesign = {
  id: "guyed-montville-370ft",
  name: "Guyed Lattice (Montville 370 ft)",
  description: "Real 370 ft triangular guyed lattice, Montville CT (AT&T CT2049, TIA-222-G).",
  author: "reference: CT Montville Old Colchester 370 ft tnxTower SA",
  spec: {
    type: "guyed",
    height: H,
    stack: {
      faces: 3, // three-face triangular
      panels: 30, // 15 sections × 2 panels ≈ 3.76 m panels
      baseRadius: 0.88, // 5.00 ft face / √3, CONSTANT
      topRadius: 0.88,
      legSection: LEG,
      braceSection: "braceMed", // angle + solid-round bracing
      legMaterial: "a36",
      braceMaterial: "a36",
      bracing: "X",
    },
    guys: [
      // [attach frac (est.), anchor radius (EXACT, m)]
      { atHeightFrac: 0.28, anchorRadius: 34.87, section: "guy12", material: "guyStrand", pretensionStrain: 0.001 },
      { atHeightFrac: 0.53, anchorRadius: 59.02, section: "guy12", material: "guyStrand", pretensionStrain: 0.001 },
      { atHeightFrac: 0.73, anchorRadius: 68.52, section: "guy12", material: "guyStrand", pretensionStrain: 0.001 },
      { atHeightFrac: 0.9, anchorRadius: 75.33, section: "guy12", material: "guyStrand", pretensionStrain: 0.001 },
    ],
    foundation: {
      soil: "stiff-clay", // New London County
      base: { type: "pad", width: 3, diameter: 1, depth: 2.5, concrete: "c25" }, // central base pier
      anchor: { type: "deadman", width: 3, diameter: 1, depth: 3, concrete: "c25" }, // 12 concrete anchor blocks
    },
  },
};
